import { toast } from 'sonner';

import { extractServerError } from '@/lib/api-error';
import { Sentry } from '@/lib/sentry';

/**
 * Wire window-level handlers for soft errors that the React boundary cannot
 * catch by design:
 *   - `unhandledrejection` - a Promise rejected without a .catch (e.g. a
 *     mutation handler that forgot to await).
 *   - `error` - uncaught throws outside the React tree (event handlers running
 *     synchronously, async setTimeout callbacks, etc.).
 *
 * We surface these as toasts rather than escalating to the boundary so the app
 * stays usable. Anything that breaks rendering will still hit the boundary
 * automatically.
 */
export function installWindowErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  // Set of error signatures we've already reported recently, so a thrashing
  // mutation doesn't flood the screen.
  const recentSignatures = new Set<string>();
  const remember = (sig: string): boolean => {
    if (recentSignatures.has(sig)) return false;
    recentSignatures.add(sig);
    setTimeout(() => recentSignatures.delete(sig), 4000);
    return true;
  };

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;

    // API-envelope rejections (`{ statusCode, message, code, … }`) are the
    // axios-interceptor's way of rejecting on 4xx/5xx - TanStack Query's
    // `mutateAsync` re-rejects after the mutation's `onError` has already
    // toasted. Treat those as handled and don't double-toast. The mutation's
    // own `onError` is responsible for the user-facing message.
    if (isApiEnvelope(reason)) {
      event.preventDefault();
      return;
    }

    const message = extractMessage(reason);
    if (!message || !remember(`r:${message}`)) return;
    toast.error(message, {
      description: 'An unhandled promise rejection slipped past a mutation handler.',
      duration: 8000,
    });

    console.error('[unhandledrejection]', reason);
    Sentry.withScope((scope) => {
      scope.setTag('source', 'unhandledrejection');
      Sentry.captureException(reason);
    });
  });

  window.addEventListener('error', (event) => {
    // Filter ResizeObserver loop errors (Chrome) - they are noise.
    if (typeof event.message === 'string' && event.message.includes('ResizeObserver loop')) return;

    const reported = unwrapCause(event.error);
    const message = extractMessage(reported) ?? event.message;
    if (!message || !remember(`e:${message}`)) return;
    toast.error(message, {
      description:
        reported === event.error
          ? 'An uncaught error fired outside the React tree.'
          : 'React recovered from this by re-rendering, but it is still a real error.',
      duration: 8000,
    });

    // Log the wrapper too - its message says how React reacted, which the
    // unwrapped error on its own doesn't tell you.
    console.error('[window.onerror]', reported ?? event.message, {
      wrapper: reported === event.error ? undefined : event.error,
    });
    Sentry.withScope((scope) => {
      scope.setTag('source', 'window-onerror');
      Sentry.captureException(reported ?? event.message);
    });
  });
}

/**
 * The error React actually hit, out of the one it reports.
 *
 * React 19 does not hand a recoverable error to `window.onerror` directly - it
 * reports a wrapper whose message describes its own recovery ("There was an
 * error during concurrent rendering but React was able to recover by instead
 * synchronously rendering the entire root") and hangs the real error off
 * `cause`. Toasting the wrapper tells nobody what broke, so follow the chain.
 */
function unwrapCause(error: unknown): unknown {
  let current = error;
  // Bounded: a cause chain is normally one link, and this must not spin on a
  // self-referential `cause`.
  for (let depth = 0; depth < 5; depth++) {
    const cause = current instanceof Error ? (current.cause as unknown) : undefined;
    if (cause === undefined || cause === null || cause === current) return current;
    current = cause;
  }
  return current;
}

function extractMessage(value: unknown): string | null {
  return extractServerError(value).message;
}

/**
 * Detect the shared `ApiError` envelope shape produced by the API + axios
 * interceptor (see `apps/admin/src/lib/api-client.ts`). The envelope always
 * carries a numeric `statusCode` and a string `message`; anything matching
 * is a handled-at-the-mutation-layer error, not a programming bug.
 */
function isApiEnvelope(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const obj = value as { statusCode?: unknown; message?: unknown };
  return typeof obj.statusCode === 'number' && typeof obj.message === 'string';
}
